// src/features/team/SpiritPicker.tsx
import { pinyin } from 'pinyin-pro';
import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import type { Spirit } from '../../core/types';
import { SPIRITS } from '../../data/mock-spirits';

import { ATTRIBUTE_COLOR_MAP } from '../../data/attributes';

function getTypeStyle(type: string): string {
  const color = ATTRIBUTE_COLOR_MAP[type];
  if (!color) return 'bg-slate-500/20 text-slate-300 border-slate-500/40';
  // 用 inline style 更精确，但为了 class 统一，这里只借用颜色 token
  return 'border';
}

interface SpiritPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (spiritId: string) => void;
  currentId?: string | null;
}

export function SpiritPicker({
  open,
  onClose,
  onSelect,
  currentId,
}: SpiritPickerProps) {
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  // 提取所有出现过的属性，用于筛选栏
  const allTypes = useMemo(() => {
    const set = new Set<string>();
    SPIRITS.forEach((s) => s.types.forEach((t) => set.add(t)));
    return Array.from(set);
  }, []);

    // 预生成拼音索引：id -> { full, first }
  const pinyinIndex = useMemo(() => {
    const map = new Map<string, { full: string; first: string }>();
    SPIRITS.forEach((s) => {
      const full = pinyin(s.name, {
        toneType: 'none',
        type: 'array',
      })
        .join('')
        .toLowerCase();
      const first = pinyin(s.name, {
        pattern: 'first',
        toneType: 'none',
        type: 'array',
      })
        .join('')
        .toLowerCase();
      map.set(s.id, { full, first });
    });
    return map;
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SPIRITS.filter((s) => {
      const matchType = !typeFilter || s.types.includes(typeFilter);
      if (!matchType) return false;
      if (!q) return true;

      // 1. 原始汉字 / id 匹配
      if (
        s.name.toLowerCase().includes(q) ||
        s.id.toLowerCase().includes(q)
      ) {
        return true;
      }

      // 2. 拼音全拼匹配（例如 "dimo"）
      const py = pinyinIndex.get(s.id);
      if (!py) return false;
      if (py.full.includes(q)) return true;
      // 3. 拼音首字母匹配（例如 "dm"）
      if (py.first.includes(q)) return true;

      return false;
    });
  }, [query, typeFilter, pinyinIndex]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-xl font-bold text-white">选择精灵</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition"
            aria-label="关闭"
          >
            <X size={20} />
          </button>
        </div>

        {/* 搜索栏 */}
        <div className="px-6 py-3 border-b border-slate-700 space-y-3">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索名称 / 拼音 / 首字母 / ID..."
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-primary-500"
              autoFocus
            />
          </div>

          {/* 属性筛选 */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setTypeFilter(null)}
              className={`px-3 py-1 text-xs rounded-full border transition ${
                typeFilter === null
                  ? 'bg-primary-600 text-white border-primary-500'
                  : 'bg-slate-700 text-slate-300 border-slate-600 hover:bg-slate-600'
              }`}
            >
              全部
            </button>
            {allTypes.map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`px-3 py-1 text-xs rounded-full border transition ${
                  typeFilter === t
                    ? 'bg-primary-600 text-white border-primary-500'
                    : getTypeStyle(t) + ' hover:opacity-80'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* 精灵网格 */}
        <div className="flex-1 overflow-y-auto p-6">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-12">没有符合条件的精灵</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
              {filtered.map((spirit) => (
                <SpiritCard
                  key={spirit.id}
                  spirit={spirit}
                  selected={currentId === spirit.id}
                  onClick={() => {
                    onSelect(spirit.id);
                    onClose();
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** 单个精灵卡片 */
function SpiritCard({
  spirit,
  selected,
  onClick,
}: {
  spirit: Spirit;
  selected: boolean;
  onClick: () => void;
}) {
  const typeColors = spirit.types.map(
    (t) => ATTRIBUTE_COLOR_MAP[t] ?? '#94a3b8',
  );

  return (
    <button
      onClick={onClick}
      className={`text-left p-3 rounded-xl border transition group ${
        selected
          ? 'border-primary-500 bg-primary-500/10'
          : 'border-slate-700 bg-slate-900/50 hover:border-slate-500 hover:bg-slate-900'
      }`}
    >
      {/* 图片区 */}
      <div className="relative aspect-square mb-3 rounded-lg bg-slate-800 overflow-hidden flex items-center justify-center">
        {spirit.imageUrl ? (
          <img
            src={spirit.imageUrl}
            alt={spirit.name}
            loading="lazy"
            className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform"
          />
        ) : (
          <span className="text-xs text-slate-600">无图</span>
        )}
        <span className="absolute top-1 left-1 text-[10px] font-mono text-slate-400 bg-slate-900/70 px-1.5 py-0.5 rounded">
          {String(spirit.id).padStart(3, '0')}
        </span>
      </div>

      {/* 名称 */}
      <div className="mb-2">
        <span className="font-semibold text-white text-sm">{spirit.name}</span>
      </div>

      {/* 属性标签 */}
      <div className="flex flex-wrap gap-1 mb-3">
        {spirit.types.map((t, i) => (
          <span
            key={t}
            className="px-2 py-0.5 text-[10px] rounded-full border"
            style={{
              color: typeColors[i],
              borderColor: typeColors[i] + '66',
              backgroundColor: typeColors[i] + '22',
            }}
          >
            {t}
          </span>
        ))}
      </div>

      {/* 种族值 */}
      <div className="grid grid-cols-3 gap-1 text-[10px] text-slate-400">
        <span>HP {spirit.baseStats.hp}</span>
        <span>攻 {spirit.baseStats.atk}</span>
        <span>防御 {spirit.baseStats.def}</span>
        <span>魔攻 {spirit.baseStats.spa}</span>
        <span>魔防 {spirit.baseStats.spd}</span>
        <span>速度 {spirit.baseStats.spe}</span>
      </div>
    </button>
  );
}